/**
 * AssessmentStepShell.js
 *
 * Shared layout for every screen in the Assessment Wizard:
 *   brand header → progress bar + step counter → question title →
 *   supporting description → step-specific content (children) →
 *   Previous/Next footer.
 *
 * Individual steps (GoalStep, BiometricsStep, …) render their own fields as
 * `children` and stay focused on their own state/validation — this shell
 * owns only the chrome that's identical across all 12 steps.
 */

import React from "react";
import {
  Section,
  AppShell,
  Header,
  Brand,
  ProgressRow,
  ProgressTrack,
  ProgressFill,
  StepCounter,
  QuestionTitle,
  QuestionDesc,
  StepContent,
  FooterNav,
  PreviousButton,
  NextButton,
} from "./AssessmentStepShell.styles";

/**
 * @param {object}   props
 * @param {number}   props.step          - Current step (1-indexed).
 * @param {number}   props.totalSteps    - Total number of steps in the wizard.
 * @param {string}   props.title         - Question title, e.g. "What's your goal?"
 * @param {string}   [props.description] - Supporting one-line description.
 * @param {function} props.onPrevious    - Called when Previous is clicked.
 * @param {function} props.onNext        - Called when Next is clicked.
 * @param {boolean}  [props.previousDisabled] - Disables/hides Previous (step 1).
 * @param {boolean}  [props.nextDisabled]     - Disables Next until the step is valid.
 * @param {string}   [props.nextLabel]        - Override for the Next button label.
 * @param {React.ReactNode} props.children    - Step-specific content (e.g. card grid).
 */
const AssessmentStepShell = ({
  step,
  totalSteps,
  title,
  description,
  onPrevious,
  onNext,
  previousDisabled = false,
  nextDisabled = false,
  nextLabel = "Next",
  children,
}) => {
  const percent = totalSteps > 0 ? (step / totalSteps) * 100 : 0;

  return (
    <Section>
      <AppShell>
        {/* <Header>
          <Brand>YogaShastra</Brand>
        </Header> */}

        <ProgressRow
          role="progressbar"
          aria-valuenow={step}
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          aria-label={`Step ${step} of ${totalSteps}`}
        >
          <ProgressTrack>
            <ProgressFill $percent={percent} />
          </ProgressTrack>
          <StepCounter>
            Step {step} of {totalSteps}
          </StepCounter>
        </ProgressRow>

        <QuestionTitle>{title}</QuestionTitle>
        {description && <QuestionDesc>{description}</QuestionDesc>}

        <StepContent>{children}</StepContent>

        <FooterNav>
          <PreviousButton
            type="button"
            onClick={onPrevious}
            disabled={previousDisabled}
            aria-label="Previous step"
          >
            ◂ Previous
          </PreviousButton>
          <NextButton
            type="button"
            onClick={onNext}
            disabled={nextDisabled}
            aria-label="Next step"
          >
            {nextLabel} ▸
          </NextButton>
        </FooterNav>
      </AppShell>
    </Section>
  );
};

AssessmentStepShell.displayName = "AssessmentStepShell";
export default React.memo(AssessmentStepShell);
